import { useEffect, useState } from "react";
import { FinalizarCompraGiroSaldo } from "./FinalizarCompraGiroSaldo";
import { FinalizarCompraGiroPix } from "./FinalizarCompraGiroPix";
import AcessoService, { PresenteadoDto } from "@/services/AcessoService";
import { FinalizarCompraGiroCartaoCredito } from "./FinalizarCompraGiroCartaoCredito";
import CompraService from "@/services/CompraService";
import { FormasPagamento } from "@/dtos/FormasPagamento";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import clsx from "clsx";
import { Coins, CreditCard } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppService from "@/services/AppService";
import { FinalizarCompraCartaoCreditoForm } from "@/pages/FinalizarCompra/FinalizarCompraCartaoCredito.Form";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import StorageUtils from "@/utils/StorageUtils";
import { labelSingularLower } from "@/utils/copyTituloBilhete";
import { SelecaoCompraGiro } from "@/components/CompraRapidaGiro";
import { DadosResumoGiro } from "@/dtos/Request/FinalizarCompraSorteioGiroDto";
import SorteioService from "@/services/SorteioService";
import { SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import { getSelecaoGiroCheckout, clearSelecaoGiroCheckout } from "@/utils/selecaoGiroCheckout";
import { montarPayloadSorteioGiro } from "@/utils/montarPayloadSorteioGiro";
import { BuscarCautelasCarrinhoDto } from "@/dtos/Request/RequestBuscarCautelasCarrinho";

interface DadosCartaoCredito {
    nome: string;
    numeroCartao: string;
    dataVencimento: string;
    codSeguranca: string;
}

function montarDadosResumo(selecao: SelecaoCompraGiro, sorteioGiro: SorteioGiroDto): DadosResumoGiro {
    return {
        quantidade: selecao.quantidade,
        valorTotal: selecao.valor,
        dataSorteio: sorteioGiro.DataFormatada,
        edicao: sorteioGiro.Edicao,
    };
}

export function FinalizarCompraGiroPage() {
    const [formaPagamento, setFormaPagamento] = useState<FormasPagamento>(FormasPagamento.Pix);
    const [saldoDisponivel, setSaldoDisponivel] = useState(0);
    const [dadosResumo, setDadosResumo] = useState<DadosResumoGiro | null>(null);
    const [selecao, setSelecao] = useState<SelecaoCompraGiro | null>(null);
    const [loading, setLoading] = useState(true);
    const [podeRealizarPagamentoCartaoCredito, setPodeRealizarPagamentoCartaoCredito] = useState(true);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState("");
    const [presenteados, setPresenteados] = useState<PresenteadoDto[]>([]);

    const isAppRoute =
        location.pathname.toLowerCase().includes("/finalizarcompraapp") ||
        location.pathname.toLowerCase().includes("/finalizarcompragiroapp");

    useEffect(() => {
        if (isAppRoute) {
            const match = window.location.href.match(/(?:\?|&)token=([^&]+)/);
            const searchParamToken = match ? match[1] : null;

            if (searchParamToken !== null) {
                setToken(searchParamToken);
            } else {
                navigate("/");
            }
        } else {
            void initialLoad();
        }
    }, []);

    useEffect(() => {
        if (token) {
            void initialLoadApp();
        }
    }, [token]);

    async function handlePresenteados(action: "remove" | "load", presenteado?: PresenteadoDto) {
        switch (action) {
            case "remove":
                if (token) {
                    await AppService.removerPresenteado(presenteado!.IdUsuario, token);
                } else {
                    await AcessoService.removerPresenteado(presenteado!.IdUsuario);
                }
                await handlePresenteados("load");
                break;
            case "load":
                if (token) {
                    const { Data: result } = await AppService.buscarPresenteados(token);
                    setPresenteados(result);
                } else {
                    const { Data: result } = await AcessoService.buscarPresenteados();
                    setPresenteados(result);
                }
                break;
        }
    }

    async function carregarSelecaoESorteio(): Promise<{
        selecao: SelecaoCompraGiro;
        dadosResumo: DadosResumoGiro;
    } | null> {
        const selecaoPersistida = getSelecaoGiroCheckout();
        if (!selecaoPersistida) {
            navigate("/compra/titulos?tab=giro");
            return null;
        }

        const { Data: sorteioVigente } = await SorteioService.buscarSorteioVigente();
        const sorteioGiro = sorteioVigente.SorteioGiro;
        if (!sorteioGiro) {
            clearSelecaoGiroCheckout();
            navigate("/compra/titulos");
            return null;
        }

        setSelecao(selecaoPersistida);
        const resumo = montarDadosResumo(selecaoPersistida, sorteioGiro);
        setDadosResumo(resumo);
        return { selecao: selecaoPersistida, dadosResumo: resumo };
    }

    async function initialLoadApp() {
        const resultado = await carregarSelecaoESorteio();
        if (!resultado) {
            return;
        }

        const { Data: saldo } = await AppService.buscarSaldoCreditoApp(token);
        setSaldoDisponivel(saldo);

        const { Data: cartaoHabilitado } = await AppService.VerificarCartaoCreditoHabilitadoApp(token);
        setPodeRealizarPagamentoCartaoCredito(cartaoHabilitado);

        const { Data: presenteadosResult } = await AppService.buscarPresenteados(token);
        setPresenteados(presenteadosResult);

        if (saldo >= resultado.dadosResumo.valorTotal) {
            setFormaPagamento(FormasPagamento.Saldo);
        }

        setLoading(false);
    }

    async function initialLoad() {
        const resultado = await carregarSelecaoESorteio();
        if (!resultado) {
            return;
        }

        const { Data: saldo } = await AcessoService.buscarSaldoCredito();
        setSaldoDisponivel(saldo);

        const { Data: cartaoHabilitado } = await CompraService.VerificarCartaoCreditoHabilitado();
        setPodeRealizarPagamentoCartaoCredito(cartaoHabilitado);

        const { Data: presenteadosResult } = await AcessoService.buscarPresenteados();
        setPresenteados(presenteadosResult);

        if (saldo >= resultado.dadosResumo.valorTotal) {
            setFormaPagamento(FormasPagamento.Saldo);
        }

        setLoading(false);
    }

    const formikFormFinalizarCompraCartaoCredito = useFormik<DadosCartaoCredito>({
        initialValues: {
            nome: "",
            numeroCartao: "",
            dataVencimento: "",
            codSeguranca: "",
        },
        validationSchema: Yup.object({
            nome: Yup.string()
                .min(3, "Deve possuir ao menos 5 caracteres")
                .test("Validar nome", "Insira nome que consta no cartão de crédito", (valueToValidate) => {
                    if (!valueToValidate) {
                        return false;
                    }
                    if (
                        valueToValidate.indexOf(" ") === -1 ||
                        valueToValidate.indexOf(" ") === valueToValidate.length - 1
                    ) {
                        return false;
                    }
                    return true;
                }),
            numeroCartao: Yup.string()
                .required("Obrigatório")
                .test("Validar Cartão de Crédito", "Cartão de Crédito Inválido", (valuepToValidate) => {
                    if (valuepToValidate.replace(/_/g, "").replace(/\./g, "").length !== 16) {
                        return false;
                    }
                    return true;
                }),
            dataVencimento: Yup.string()
                .required("Obrigatório")
                .test("Validar data vencimento", "Data Inválida", (valueToValidate) => {
                    if (valueToValidate.replace(/_/g, "").replace(/\//g, "").length !== 4) {
                        return false;
                    }
                    const mes = parseInt(valueToValidate.split("/")[0]);
                    if (mes === 0 || mes > 12) {
                        return false;
                    }
                    const ano = parseInt("20" + valueToValidate.split("/")[1]);
                    const dataAtual = new Date();
                    if (dataAtual.getFullYear() === ano && mes < dataAtual.getMonth() + 1) {
                        return false;
                    }
                    if (ano < dataAtual.getFullYear()) {
                        return false;
                    }
                    return true;
                }),
            codSeguranca: Yup.string()
                .required("Obrigatório")
                .test("Validar Cod. Segurança", "Cod. Inválido", (valuepToValidate) => {
                    if (valuepToValidate.replace(/_/g, "").length !== 3) {
                        return false;
                    }
                    return true;
                }),
        }),
        onSubmit: async (values) => {
            if (!selecao || !dadosResumo) {
                return;
            }

            try {
                const mes = values.dataVencimento.split("/")[0];
                const ano = values.dataVencimento.split("/")[1];

                let idPresenteado: number | undefined;
                const getIdPessoaPresenteada = searchParams.get("idPessoaPresenteada");
                if (getIdPessoaPresenteada) {
                    idPresenteado = Number(getIdPessoaPresenteada);
                }

                const payload = montarPayloadSorteioGiro(selecao, {
                    Origem: token ? "IOS" : "Web",
                    FormaPagamento: formaPagamento,
                    IdentificadorDispositivo: await StorageUtils.getDispositivo(),
                    IdPresenteado: idPresenteado,
                    IdVendedor: StorageUtils.getIdVendedor(),
                    TitularCartao: values.nome,
                    NumeroCartao: values.numeroCartao.replace(/_/g, "").replace(/\./g, ""),
                    VerificadorCartao: values.codSeguranca,
                    VencimentoCartao: values.dataVencimento,
                    VencimentoAno: ano,
                    VencimentoMes: mes,
                    Data: "",
                });

                if (token) {
                    await AppService.FinalizarCompraSorteioGiroCartaoCreditoApp(payload, token);
                    toast({
                        variant: "success",
                        description: `Sua compra está em análise pelo cartão. A confirmação da compra será enviada para o seu e-mail. Feche o navegador e acesse o histórico de compras em seu aplicativo para visualizar seu ${labelSingularLower()}`,
                        title: "Compra em avaliação",
                        duration: 6000,
                    });
                } else {
                    await CompraService.FinalizarCompraSorteioGiroCartaoCredito(payload);
                    toast({
                        variant: "success",
                        description:
                            "Sua compra está em análise pelo cartão. A confirmação da compra será enviada para o seu e-mail",
                        title: "Compra em avaliação",
                        duration: 10000,
                    });
                    navigate("/compra/titulos?tab=giro");
                }

                clearSelecaoGiroCheckout();
                StorageUtils.removeIdVendedor();
            } catch (error: unknown) {
                toast({
                    variant: "destructive",
                    description: String(error),
                    duration: 4000,
                });
            }
        },
    });

    if (loading || !dadosResumo || !selecao) {
        return (
            <div className="ms-4 mb-20 px-3 lg:px-0">
                <Skeleton className="h-10 w-44" />
                <Skeleton className="h-10 w-96 mt-1" />
                <Skeleton className="h-10 w-56 mt-4" />
                <div className="flex mt-2">
                    <Skeleton className="h-14 w-2/4" />
                    <Skeleton className="h-72 w-2/4 ms-4" />
                </div>
            </div>
        );
    }

    const dadosResumoForm = {} as BuscarCautelasCarrinhoDto;

    return (
        <div className="ms-4 mb-10 px-3 lg:px-0">
            <h1 className="text-primary font-bold text-2xl">Finalizar Compra — Sorteio Giro</h1>
            <p>Para concluir a compra, selecione uma forma de pagamento</p>

            <div className="flex flex-col md:flex-row gap-5 mt-6">
                <div className="flex flex-col md:w-7/12">
                    <div className="flex flex-rol justify-start gap-4 mb-10">
                        <Card
                            onClick={() => setFormaPagamento(FormasPagamento.Pix)}
                            className={clsx("w-36 h-36 cursor-pointer hover:bg-buttondefault/80", {
                                "bg-buttondefault":
                                    formaPagamento?.toString() === FormasPagamento.Pix.toString(),
                            })}
                        >
                            <CardHeader className="flex text-center text-buttondefault-foreground">
                                <CardTitle>Pix</CardTitle>
                            </CardHeader>
                            <CardContent className="flex justify-center text-buttondefault-foreground">
                                <Coins />
                            </CardContent>
                        </Card>

                        {podeRealizarPagamentoCartaoCredito && (
                            <Card
                                onClick={() => setFormaPagamento(FormasPagamento.CartaoCredito)}
                                className={clsx("w-52 h-36 cursor-pointer hover:bg-buttondefault/80", {
                                    "bg-buttondefault":
                                        formaPagamento?.toString() ===
                                        FormasPagamento.CartaoCredito.toString(),
                                })}
                            >
                                <CardHeader className="flex text-center text-buttondefault-foreground">
                                    <CardTitle>Cartão de Crédito</CardTitle>
                                </CardHeader>
                                <CardContent className="flex justify-center text-buttondefault-foreground">
                                    <CreditCard />
                                </CardContent>
                            </Card>
                        )}

                        {saldoDisponivel > 0 && (
                            <Card
                                onClick={() => setFormaPagamento(FormasPagamento.Saldo)}
                                className={clsx("w-52 h-36 cursor-pointer hover:bg-buttondefault/80", {
                                    "bg-buttondefault":
                                        formaPagamento?.toString() === FormasPagamento.Saldo.toString(),
                                })}
                            >
                                <CardHeader className="flex text-center text-buttondefault-foreground">
                                    <CardTitle>Saldo de Crédito</CardTitle>
                                </CardHeader>
                                <CardContent className="flex justify-center text-buttondefault-foreground">
                                    <Coins />
                                </CardContent>
                            </Card>
                        )}

                        {podeRealizarPagamentoCartaoCredito && (
                            <Card
                                onClick={() => setFormaPagamento(FormasPagamento.AuxilioEmergencial)}
                                className={clsx("w-40 h-36 cursor-pointer hover:bg-buttondefault/80", {
                                    "bg-buttondefault":
                                        formaPagamento?.toString() ===
                                        FormasPagamento.AuxilioEmergencial.toString(),
                                })}
                            >
                                <CardHeader className="flex text-center text-buttondefault-foreground">
                                    <CardTitle>Auxilio Emergencial Caixa</CardTitle>
                                </CardHeader>
                                <CardContent className="flex justify-center text-buttondefault-foreground">
                                    <CreditCard />
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {(formaPagamento === FormasPagamento.CartaoCredito ||
                        formaPagamento === FormasPagamento.AuxilioEmergencial) && (
                        <FinalizarCompraCartaoCreditoForm
                            formik={formikFormFinalizarCompraCartaoCredito}
                            dadosResumo={dadosResumoForm}
                            formaPagamento={formaPagamento}
                        />
                    )}

                    {formaPagamento === FormasPagamento.Saldo && (
                        <div className="flex flex-col md:flex-row justify-between">
                            <div className="md:w-6/12">
                                <h2 className="text-xl text-primary-foreground mb-5">
                                    Saldo disponível:{" "}
                                    {saldoDisponivel.toLocaleString("pt-br", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                </h2>
                                {saldoDisponivel < dadosResumo.valorTotal && (
                                    <div className="flex mt-2">
                                        <Button className="w-full sm:w-56">Comprar Créditos</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {(formaPagamento === FormasPagamento.CartaoCredito ||
                    formaPagamento === FormasPagamento.AuxilioEmergencial) && (
                    <FinalizarCompraGiroCartaoCredito
                        handlePresenteados={handlePresenteados}
                        presenteados={presenteados}
                        dadosResumo={dadosResumo}
                        formik={formikFormFinalizarCompraCartaoCredito}
                    />
                )}

                {formaPagamento === FormasPagamento.Saldo && (
                    <FinalizarCompraGiroSaldo
                        handlePresenteados={handlePresenteados}
                        presenteados={presenteados}
                        token={token}
                        dadosResumo={dadosResumo}
                        selecao={selecao}
                        saldoDisponivel={saldoDisponivel}
                    />
                )}

                {formaPagamento === FormasPagamento.Pix && (
                    <FinalizarCompraGiroPix
                        handlePresenteados={handlePresenteados}
                        presenteados={presenteados}
                        token={token}
                        dadosResumo={dadosResumo}
                        selecao={selecao}
                    />
                )}
            </div>
        </div>
    );
}
